import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useEscrowAccount from '@/hooks/useEscrowAccount';
import { useMemberInfo } from '@/hooks/useMemberInfo';
import { useUserProfile } from '@/hooks/useUserProfile';
import { UserInfo } from '@/services/types/common';
import { SubMemberType } from '@/utils/constants/sub-account';

export const useUserInfo = (isEdit?: boolean) => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const subAccountId = search.get('subaccount_id');
  const name = search.get('name');
  const memberType = search.get('memberType');
  const memberStatus = search.get('memberStatus') || '';
  const subAccountMemberTag = search.get('member_tag') || '';
  const isEarnFundAccount = memberType === SubMemberType.EARN_FUND_ACCOUNT;
  const isEntrustTrade =
    memberType === SubMemberType.TRUST_ACCOUNT || isEarnFundAccount;
  const editSubAccountId = search.get('subAccountId');
  const {
    loading,
    userInfo,
    isDemoAccount,
    is_copy_pro_sub_account,
    isCopyTrader,
    isIslamicAccount,
    isUTA2: curLoginAccountIsUta2,
  } = useUserProfile();

  const { institutionInfoCode } = useEscrowAccount();

  const {
    hasCopyTradeBrokerTag,
    copyTradeUpgradeSuccess,
    loading: memberInfoApiLoading,
  } = useMemberInfo();

  const apiManagementId = search.get('apiManagementId') as string;
  // 是否是查询子账号
  const isSubAccount = useMemo(
    () => (userInfo as UserInfo)?.is_submember || !!subAccountId,
    [userInfo, subAccountId],
  );
  // 是否是子账号登陆
  const isSubAccountLogin = useMemo(
    () => !!(userInfo as UserInfo)?.is_submember,
    [userInfo],
  );

  const memberId = useMemo(
    () => subAccountId || (userInfo as UserInfo)?.id,
    [userInfo, subAccountId],
  );

  const hasGoogle2fa = useMemo(() => {
    return (userInfo as UserInfo)?.has_google2fa;
  }, [userInfo]);

  const emailVerified = useMemo(
    () => (userInfo as UserInfo)?.email_verified,
    [userInfo],
  );

  const isCopypro = useMemo(() => {
    return (
      search.get('memberType') === SubMemberType.COPY_PRO_ACCOUNT ||
      is_copy_pro_sub_account
    );
  }, [search, is_copy_pro_sub_account]);

  const backApiManagement = () => {
    let baseUrl = subAccountId
      ? '/app/user/subaccount-api-management'
      : '/app/user/api-management';

    const memberTag = subAccountMemberTag
      ? `&member_tag=${subAccountMemberTag}`
      : '';

    if (!isEdit) {
      if (subAccountId) {
        baseUrl += `?subaccount_id=${subAccountId}&name=${name}${memberTag}`;
        if (memberType) {
          baseUrl += `&memberType=${memberType}`;
        }
        if (memberStatus) {
          baseUrl += `&memberStatus=${memberStatus}`;
        }
        navigate(baseUrl);
      } else {
        navigate(baseUrl);
      }
      return;
    }
    if (editSubAccountId) {
      baseUrl += `?subaccount_id=${editSubAccountId}&name=${name}${memberTag}`;

      if (memberType) {
        baseUrl += `&memberType=${memberType}`;
      }
      if (memberStatus) {
        baseUrl += `&memberStatus=${memberStatus}`;
      }
      navigate(baseUrl);
    } else {
      navigate(baseUrl);
    }
  };

  const isUTA2 = useMemo(() => {
    if (subAccountId || editSubAccountId) {
      return subAccountMemberTag.includes('uta_inverse');
    }
    return curLoginAccountIsUta2;
  }, [
    curLoginAccountIsUta2,
    subAccountMemberTag,
    editSubAccountId,
    subAccountId,
  ]);

  const isCopyTradingBrokerSubAccount = useMemo(() => {
    // 如果是托管商用户，则不走broker逻辑
    if (institutionInfoCode) {
      return false;
    }

    // 如果当前登录的是子账号
    if (userInfo.is_submember) {
      return hasCopyTradeBrokerTag && copyTradeUpgradeSuccess;
    }
    // 如果当前登录的是母账号，根据URL上是否有subaccount_id参数来判断是否为子账号
    if (subAccountId || editSubAccountId) {
      return hasCopyTradeBrokerTag && memberStatus?.indexOf('upgraded') > -1;
    }
    return false;
  }, [
    hasCopyTradeBrokerTag,
    copyTradeUpgradeSuccess,
    subAccountId,
    memberStatus,
    userInfo.is_submember,
    editSubAccountId,
    institutionInfoCode,
  ]);
  // 普通子账号
  const isCommonSubAccount = useMemo(() => {
    if (!isSubAccountLogin) {
      const memberType = search.get('memberType');
      const isCopyTrade = subAccountMemberTag?.includes('copytrade');
      return (
        (memberType === SubMemberType.ORDINARY_ACCOUNT ||
          memberType === SubMemberType.TRUST_ACCOUNT) &&
        !isCopyTrade
      );
    }

    return (
      !isCopyTrader &&
      !is_copy_pro_sub_account &&
      !isDemoAccount &&
      !isIslamicAccount
    );
  }, [
    search,
    isSubAccountLogin,
    isCopyTrader,
    is_copy_pro_sub_account,
    isDemoAccount,
    isIslamicAccount,
    subAccountMemberTag,
  ]);

  return {
    subAccountId,
    loading,
    isEntrustTrade,
    memberId,
    editSubAccountId,
    apiManagementId,
    isSubAccount,
    hasGoogle2fa,
    emailVerified,
    isSubAccountLogin,
    backApiManagement,
    isDemoAccount,
    isCopypro,
    isUTA2,
    isCopyTradingBrokerSubAccount,
    memberInfoApiLoading,
    isCommonSubAccount,
    isEarnFundAccount,
  };
};
